import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Shield, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { WHATSAPP_LINK } from "../config/contact";

const NavLink = ({ href, children, className = "" }) => (
  <a href={href} className={`text-sm font-medium text-foreground/70 hover:text-primary transition-colors ${className}`}>{children}</a>
);

export default function Header({ 
  currentPage = "rescate", 
  internalLinks = [], 
  ctaText = "Contacto", 
  ctaHref = "#contacto",
  ctaColor = "primary",
  showLanguageSelector = false,
  LanguageSelector = null
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const servicePages = {
    rescate: { name: "Rescate", path: "/", color: "text-primary" },
    mantenimiento: { name: "Mantenimiento", path: "/mantenimiento-wordpress", color: "text-secondary" },
    optimizacion: { name: "Optimización", path: "/optimizacion-velocidad-wordpress", color: "text-primary" },
    hosting: { name: "Hosting", path: "/hosting-wordpress", color: "text-primary" },
    blog: { name: "Blog", path: "/blog", color: "text-secondary" }
  };
  
  const buttonColors = {
    primary: "bg-primary hover:bg-primary/90",
    secondary: "bg-secondary hover:bg-secondary/90",
    green: "bg-secondary hover:bg-secondary/90",
    orange: "bg-primary hover:bg-primary/90",
    blue: "bg-primary hover:bg-primary/90"
  };
  
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-border shadow-sm" style={{ backdropFilter: 'blur(16px) saturate(180%)', WebkitBackdropFilter: 'blur(16px) saturate(180%)' }}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <RouterLink to="/" className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-primary" />
          <span className="font-bold text-foreground text-lg">WPRescate24</span>
        </RouterLink>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {/* Service Pages Menu */}
          <div className="flex items-center gap-4 xl:gap-6 xl:pr-6 xl:border-r border-border">
            {Object.entries(servicePages).map(([key, service]) => (
              <RouterLink 
                key={key}
                to={service.path} 
                className={`text-sm font-medium ${currentPage === key ? service.color + ' hover:opacity-80' : 'text-foreground/70 hover:text-primary'} transition-colors`}
              >
                {service.name}
              </RouterLink>
            ))}
          </div>
          
          {/* Internal Page Links */}
          {internalLinks.map((link) => (
            <NavLink key={link.href} href={link.href} className="hidden xl:inline">{link.label}</NavLink>
          ))}
        </nav>
        
        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3">
          {showLanguageSelector && LanguageSelector && <LanguageSelector />}
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="text-sm text-secondary hover:text-secondary/80 font-medium">WhatsApp</a>
          <Button className={`${buttonColors[ctaColor] || buttonColors.primary} text-white font-semibold`} asChild={ctaHref.startsWith('#')}>
            {ctaHref.startsWith('#') ? <a href={ctaHref}>{ctaText}</a> : <RouterLink to={ctaHref}>{ctaText}</RouterLink>}
          </Button>
        </div>
        
        {/* Mobile Menu Button */}
        <div className="flex lg:hidden items-center gap-3">
          {showLanguageSelector && LanguageSelector && <LanguageSelector />}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-foreground p-2 hover:bg-primary/5 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-white">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            {Object.entries(servicePages).map(([key, service]) => (
              <RouterLink 
                key={key}
                to={service.path} 
                className={`text-sm font-medium ${currentPage === key ? service.color + ' hover:opacity-80' : 'text-foreground/70 hover:text-primary'} transition-colors py-2`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {service.name}
              </RouterLink>
            ))}
            
            {internalLinks.map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            
            <a 
              href={WHATSAPP_LINK} 
              target="_blank" 
              rel="noopener" 
              className="text-sm text-secondary hover:text-secondary/80 font-medium py-2"
            >
              WhatsApp
            </a>
            
            <Button 
              className={`${buttonColors[ctaColor] || buttonColors.primary} text-white font-semibold mt-2`}
              onClick={() => {
                setMobileMenuOpen(false);
                if (ctaHref.startsWith('#')) {
                  document.querySelector(ctaHref)?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              asChild={!ctaHref.startsWith('#')}
            >
              {ctaHref.startsWith('#') ? <span>{ctaText}</span> : <RouterLink to={ctaHref}>{ctaText}</RouterLink>}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
